import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { metaPixelService } from '@/services/metaPixelService';

interface UseMetaPixelProps {
  pixelId?: string;
  enabled?: boolean;
  autoTrackPageView?: boolean;
}

export const useMetaPixel = ({ 
  pixelId, 
  enabled = true, 
  autoTrackPageView = true 
}: UseMetaPixelProps = {}) => {
  // Always call useLocation at the top level
  const location = useLocation();

  const trackEvent = useCallback((eventName: string, parameters?: Record<string, any>) => {
    metaPixelService.trackEvent(eventName, parameters);
  }, []);

  const trackPageView = useCallback(() => {
    metaPixelService.trackPageView();
  }, []);

  const trackPurchase = useCallback((value: number, currency: string = 'USD', contentIds?: string[]) => {
    metaPixelService.trackPurchase(value, currency, contentIds);
  }, []);

  const trackAddToCart = useCallback((value: number, currency: string = 'USD', contentIds?: string[]) => {
    metaPixelService.trackAddToCart(value, currency, contentIds);
  }, []);

  const trackInitiateCheckout = useCallback((value: number, currency: string = 'USD', contentIds?: string[]) => {
    metaPixelService.trackInitiateCheckout(value, currency, contentIds);
  }, []);

  const trackCompleteRegistration = useCallback(() => {
    metaPixelService.trackCompleteRegistration();
  }, []);

  const trackLead = useCallback(() => {
    metaPixelService.trackLead();
  }, []);

  const trackContact = useCallback(() => {
    metaPixelService.trackContact();
  }, []);

  const trackDonation = useCallback((value: number, currency: string = 'USD', campaignId?: string) => {
    metaPixelService.trackDonation(value, currency, campaignId);
  }, []);

  const updateUserData = useCallback((userData: Record<string, any>) => {
    metaPixelService.updateUserData(userData);
  }, []);

  // Initialize Meta Pixel
  useEffect(() => {
    if (pixelId && enabled) {
      metaPixelService.initialize(pixelId, enabled);
    }
  }, [pixelId, enabled]);

  // Auto-track page views when location changes
  useEffect(() => {
    if (autoTrackPageView && metaPixelService.isAvailable() && location) {
      trackPageView();
    }
  }, [location?.pathname, autoTrackPageView, trackPageView]);

  return {
    trackEvent,
    trackPageView,
    trackPurchase,
    trackAddToCart,
    trackInitiateCheckout,
    trackCompleteRegistration,
    trackLead,
    trackContact,
    trackDonation,
    updateUserData,
    isAvailable: metaPixelService.isAvailable(),
    getConfig: metaPixelService.getConfig()
  };
}; 